import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { QrCode, Download, User, Phone, Mail, MapPin, Building, Copy } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface VCardData {
  firstName: string;
  lastName: string;
  organization: string;
  title: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const QRVCardGenerator: React.FC = () => {
  const [vCardData, setVCardData] = useState<VCardData>({
    firstName: '',
    lastName: '',
    organization: '',
    title: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [vCardString, setVCardString] = useState('');

  useSEO({
    title: 'QR vCard Generator - Create Digital Business Card QR Codes',
    description: 'Generate QR codes for digital business cards (vCard). Create professional contact QR codes with personal and business information.',
    keywords: 'qr vcard generator, digital business card, contact qr code, vcard qr, business card qr code',
    canonical: 'https://snap-tools.online/design/qr-vcard-generator'
  });

  // Generate vCard string
  useEffect(() => {
    const generateVCard = () => {
      let vcard = 'BEGIN:VCARD\\nVERSION:3.0\\n';
      
      if (vCardData.firstName || vCardData.lastName) {
        vcard += `FN:${vCardData.firstName} ${vCardData.lastName}\\n`;
        vcard += `N:${vCardData.lastName};${vCardData.firstName};;;\\n`;
      }
      
      if (vCardData.organization) {
        vcard += `ORG:${vCardData.organization}\\n`;
      }
      
      if (vCardData.title) {
        vcard += `TITLE:${vCardData.title}\\n`;
      }
      
      if (vCardData.phone) {
        vcard += `TEL:${vCardData.phone}\\n`;
      }
      
      if (vCardData.email) {
        vcard += `EMAIL:${vCardData.email}\\n`;
      }
      
      if (vCardData.website) {
        vcard += `URL:${vCardData.website}\\n`;
      }
      
      if (vCardData.address || vCardData.city || vCardData.state || vCardData.zipCode || vCardData.country) {
        const address = `;;${vCardData.address};${vCardData.city};${vCardData.state};${vCardData.zipCode};${vCardData.country}`;
        vcard += `ADR:${address}\\n`;
      }
      
      vcard += 'END:VCARD';
      
      return vcard;
    };

    const vcard = generateVCard();
    setVCardString(vcard);
    
    // Generate QR code using a free API
    if (vcard.length > 20) {
      const encodedVCard = encodeURIComponent(vcard);
      setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodedVCard}`);
    } else {
      setQrCodeUrl('');
    }
  }, [vCardData]);

  const updateField = (field: keyof VCardData, value: string) => {
    setVCardData(prev => ({ ...prev, [field]: value }));
  };

  const downloadQRCode = async () => {
    if (!qrCodeUrl) {
      toast.error('Please fill in some contact information first');
      return;
    }

    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${vCardData.firstName || 'contact'}_qr_vcard.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('QR code downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  const downloadVCard = () => {
    if (!vCardString || vCardString.length < 20) {
      toast.error('Please fill in some contact information first');
      return;
    }

    const blob = new Blob([vCardString.replace(/\\n/g, '\\r\\n')], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${vCardData.firstName || 'contact'}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('vCard file downloaded successfully!');
  };

  const copyVCard = () => {
    if (!vCardString || vCardString.length < 20) {
      toast.error('Please fill in some contact information first');
      return;
    }

    navigator.clipboard.writeText(vCardString.replace(/\\n/g, '\\n'));
    toast.success('vCard data copied to clipboard!');
  };

  const clearForm = () => {
    setVCardData({
      firstName: '',
      lastName: '',
      organization: '',
      title: '',
      phone: '',
      email: '',
      website: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    });
  };

  const loadExample = () => {
    setVCardData({
      firstName: 'John',
      lastName: 'Doe',
      organization: 'Tech Solutions Inc.',
      title: 'Software Engineer',
      phone: '+1 (555) 123-4567',
      email: 'john.doe@example.com',
      website: 'https://johndoe.dev',
      address: '123 Tech Street',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94105',
      country: 'USA'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl">
                <QrCode className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              QR vCard Generator
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Create digital business card QR codes. Generate vCard QR codes with your contact information for easy sharing.
            </p>
          </div>

          {/* Header Ad */}
          <div className="mb-12">
            <AdContainer
              adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
              className="max-w-4xl mx-auto"
              position="header"
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Contact Information</h2>
                <div className="flex gap-2">
                  <button
                    onClick={loadExample}
                    className="btn-secondary text-sm"
                  >
                    Load Example
                  </button>
                  <button
                    onClick={clearForm}
                    className="btn-secondary text-sm"
                  >
                    Clear
                  </button>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={vCardData.firstName}
                        onChange={(e) => updateField('firstName', e.target.value)}
                        className="input-field"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={vCardData.lastName}
                        onChange={(e) => updateField('lastName', e.target.value)}
                        className="input-field"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    Professional Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization
                      </label>
                      <input
                        type="text"
                        value={vCardData.organization}
                        onChange={(e) => updateField('organization', e.target.value)}
                        className="input-field"
                        placeholder="Tech Solutions Inc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Job Title
                      </label>
                      <input
                        type="text"
                        value={vCardData.title}
                        onChange={(e) => updateField('title', e.target.value)}
                        className="input-field"
                        placeholder="Software Engineer"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Contact Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={vCardData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        className="input-field"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={vCardData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        className="input-field"
                        placeholder="john.doe@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website
                      </label>
                      <input
                        type="url"
                        value={vCardData.website}
                        onChange={(e) => updateField('website', e.target.value)}
                        className="input-field"
                        placeholder="https://johndoe.dev"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Address Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={vCardData.address}
                        onChange={(e) => updateField('address', e.target.value)}
                        className="input-field"
                        placeholder="123 Tech Street"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City
                        </label>
                        <input
                          type="text"
                          value={vCardData.city}
                          onChange={(e) => updateField('city', e.target.value)}
                          className="input-field"
                          placeholder="San Francisco"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State/Province
                        </label>
                        <input
                          type="text"
                          value={vCardData.state}
                          onChange={(e) => updateField('state', e.target.value)}
                          className="input-field"
                          placeholder="CA"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ZIP/Postal Code
                        </label>
                        <input
                          type="text"
                          value={vCardData.zipCode}
                          onChange={(e) => updateField('zipCode', e.target.value)}
                          className="input-field"
                          placeholder="94105"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country
                        </label>
                        <input
                          type="text"
                          value={vCardData.country}
                          onChange={(e) => updateField('country', e.target.value)}
                          className="input-field"
                          placeholder="USA"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code & Output */}
            <div className="space-y-8">
              {/* QR Code Preview */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">QR Code</h2>
                <div className="flex flex-col items-center">
                  {qrCodeUrl ? (
                    <div className="bg-white p-4 rounded-lg shadow-lg">
                      <img 
                        src={qrCodeUrl} 
                        alt="vCard QR Code" 
                        className="w-64 h-64 border border-gray-200 rounded"
                      />
                    </div>
                  ) : (
                    <div className="w-64 h-64 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <QrCode className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Fill in contact information to generate QR code</p>
                      </div>
                    </div>
                  )}
                  
                  {qrCodeUrl && (
                    <button
                      onClick={downloadQRCode}
                      className="btn-primary mt-6 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download QR Code
                    </button>
                  )}
                </div>
              </div>

              {/* vCard Data */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">vCard Data</h2>
                {vCardString && vCardString.length > 20 ? (
                  <div>
                    <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto mb-4">
                      <code>{vCardString.replace(/\\n/g, '\\n')}</code>
                    </pre>
                    <div className="flex gap-2">
                      <button
                        onClick={copyVCard}
                        className="btn-primary flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Copy vCard
                      </button>
                      <button
                        onClick={downloadVCard}
                        className="btn-secondary flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download .vcf
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Fill in contact information to generate vCard data</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer Ad */}
          <div className="mt-12">
            <AdContainer
              adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
              className="max-w-4xl mx-auto"
              position="footer"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QRVCardGenerator;
